/**
 * TeacherContentService
 * Quản lý nội dung cá nhân hóa cho giáo viên
 */

import { wordToJsonConverter } from './WordToJsonConverter'
import { imageExtractor } from './ImageExtractor'
import type { LessonData } from './WordToJsonConverter'
import type { ContentItem } from '../../types'
import { BankQuestion } from '../bankQuestionService_moi'
import { normalizeVatliUrl } from '../../utils/urlNormalizer'
import { API_CONFIG } from '../../config/api.config'

export interface CustomContent {
  id: string
  teacherId: string
  lessonId: string
  contentType: 'theory' | 'video' | 'simulation' | 'quiz' | 'infographic'
  contentData: any
  rawJson?: string
  createdAt: string
  updatedAt: string
}

export interface ContentPreference {
  id: string
  teacherId: string
  lessonId: string
  useCustomContent: boolean
  createdAt: string
  updatedAt: string
}

class TeacherContentService {
  private readonly STORAGE_KEY_PREFIX = 'teacher_content_'
  private readonly PREFERENCE_KEY_PREFIX = 'teacher_preference_'
  // ✅ Use centralized API configuration
  private readonly API_BASE_URL = API_CONFIG.wordpress.apiUrl

  constructor() {
    // Log API URL trong dev mode để debug
    if (import.meta.env.DEV) {
      console.log('🔧 TeacherContentService API URL:', this.API_BASE_URL)
    }
  }

  private getAuthToken(): string | null {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('google_auth_token')
    if (!token) return null

    // Kiểm tra token expiration
    try {
      const parts = token.split('.')
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
        if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
          console.warn('⚠️ Token has expired:', {
            exp: new Date(payload.exp * 1000).toISOString(),
            now: new Date().toISOString()
          })
          return null // Token đã hết hạn
        }
      }
    } catch (error) {
      console.warn('⚠️ Error checking token expiration:', error)
      // Nếu không parse được, vẫn trả về token (có thể là custom token)
    }

    return token
  }

  /**
   * Upload Word document and convert to theory content
   */
  async uploadTheoryFromWord(
    teacherId: string,
    lessonId: string,
    wordFile: File,
    assetFiles?: File[]
  ): Promise<void> {
    console.log(`📤 Uploading Word file: ${wordFile.name}`)

    // 1. Convert Word to JSON
    const lessonData = await wordToJsonConverter.convertWordToJson(wordFile, teacherId, lessonId)

    // 2. Attach local assets if provided
    if (assetFiles && assetFiles.length > 0) {
      await this.attachLocalAssets(lessonData, teacherId, lessonId, assetFiles)
    }

    // 3. ✅ FIX: Fix image/video paths to include L{lop}/C{chuong}/B{bai} if missing
    this.fixContentPaths(lessonData, teacherId, lessonId)

    // 4. Save to server
    // ✅ FIX: Use saveTheoryJson (Python Backend) instead of saveCustomContent (WordPress)
    await this.saveTheoryJson(teacherId, lessonId, lessonData)

    console.log('✅ Word upload completed')
  }

  /**
   * Fix image/video paths in content to include L{lop}/C{chuong}/B{bai} if missing
   */
  private fixContentPaths(lessonData: LessonData, teacherId: string, lessonId: string): void {
    // Parse lessonId để extract lop/chuong/bai
    const lessonInfo = this.parseLessonKey(lessonId)
    if (!lessonInfo) {
      console.warn(`⚠️ Could not parse lessonId "${lessonId}", skipping path fix`)
      return
    }

    const baiPadded = lessonInfo.bai.padStart(2, '0')
    const expectedPathPrefix = `/teacher_content/${teacherId}/L${lessonInfo.lop}/C${lessonInfo.chuong}/B${baiPadded}/`

    const fixPathsInItems = (items: any[]): void => {
      if (!Array.isArray(items)) return

      for (const item of items) {
        if ((item.type === 'image' || item.type === 'video') && typeof item.content === 'string') {
          const path = item.content
          // Nếu path thiếu L/C/B structure, thêm vào
          if (path.startsWith(`/teacher_content/${teacherId}/`) && !path.includes('/L') && !path.includes('/C') && !path.includes('/B')) {
            // Extract filename từ path cũ
            const pathParts = path.split('/')
            const filename = pathParts.pop() || ''

            // Tạo path mới với L/C/B
            const newPath = `${expectedPathPrefix}${filename}`
            item.content = newPath
            if ((item as any).src) {
              (item as any).src = newPath
            }
            console.log(`🔧 [Path Fix] Fixed path: ${path} → ${newPath}`)
          }
        }

        if (Array.isArray(item.content)) {
          fixPathsInItems(item.content)
        }
      }
    }

    // Fix paths trong tất cả các phần
    if (lessonData.noiDung?.phanMoDau?.noiDung) {
      fixPathsInItems(lessonData.noiDung.phanMoDau.noiDung)
    }
    if (lessonData.noiDung?.phanNoiDung?.noiDung) {
      fixPathsInItems(lessonData.noiDung.phanNoiDung.noiDung)
    }
    if (lessonData.noiDung?.phanCuoi?.noiDung) {
      fixPathsInItems(lessonData.noiDung.phanCuoi.noiDung)
    }
  }

  /**
   * Parse lessonKey to extract lop, chuong, bai
   */
  private parseLessonKey(lessonKey: string): { lop: string; chuong: string; bai: string } | null {
    if (!lessonKey) return null

    // Format: "lop-11-chuong-1-bai-1"
    const format1Match = lessonKey.match(/lop-(\d+)-chuong-(\d+)-bai-(\d+)/i)
    if (format1Match) {
      return {
        lop: format1Match[1],
        chuong: format1Match[2],
        bai: format1Match[3]
      }
    }

    // Format: "L11_C1_B1" hoặc "L11-C1-B1"
    const format2Match = lessonKey.match(/L(\d+)[_-]C(\d+)[_-]B(\d+)/i)
    if (format2Match) {
      return {
        lop: format2Match[1],
        chuong: format2Match[2],
        bai: format2Match[3]
      }
    }

    // Format: "11-1-1" (chỉ số)
    const format3Match = lessonKey.match(/^(\d+)-(\d+)-(\d+)$/)
    if (format3Match) {
      return {
        lop: format3Match[1],
        chuong: format3Match[2],
        bai: format3Match[3]
      }
    }

    return null
  }

  /**
   * Attach local asset files to lesson content
   */
  private async attachLocalAssets(
    lessonData: LessonData,
    teacherId: string,
    lessonId: string,
    assetFiles?: File[]
  ): Promise<void> {
    if (!assetFiles || assetFiles.length === 0) {
      return
    }

    console.log(`🖼️ Attaching ${assetFiles.length} local asset(s) to lesson ${lessonId}`)

    type AssetEntry = { file: File; cachedPath?: string }
    const assetMap = new Map<string, AssetEntry>()
    assetFiles.forEach((file) => {
      const relativePath = (file as any).webkitRelativePath || file.name
      const normalizedPath = this.normalizeAssetKey(relativePath)
      const filenameKey = this.normalizeAssetKey(file.name)
      const entry: AssetEntry = { file }
      assetMap.set(normalizedPath, entry)
      if (!assetMap.has(filenameKey)) {
        assetMap.set(filenameKey, entry)
      }
    })

    await this.replaceAssetsInContent(lessonData.noiDung?.phanMoDau?.noiDung, teacherId, lessonId, assetMap)
    await this.replaceAssetsInContent(lessonData.noiDung?.phanNoiDung?.noiDung, teacherId, lessonId, assetMap)
    await this.replaceAssetsInContent(lessonData.noiDung?.phanCuoi?.noiDung, teacherId, lessonId, assetMap)
  }

  private normalizeAssetKey(key: string): string {
    return key.replace(/\\/g, '/').toLowerCase()
  }

  private async replaceAssetsInContent(
    items: ContentItem[] | undefined,
    teacherId: string,
    lessonId: string,
    assetMap: Map<string, { file: File; cachedPath?: string }>
  ): Promise<void> {
    if (!items || items.length === 0) return

    for (const item of items) {
      if ((item.type === 'image' || item.type === 'video') && typeof item.content === 'string') {
        const updatedSrc = await this.resolveAssetPath(item, teacherId, lessonId, assetMap)
        if (updatedSrc) {
          item.content = updatedSrc
            ; (item as any).src = updatedSrc
        }
      }

      if (Array.isArray(item.content)) {
        await this.replaceAssetsInContent(item.content as ContentItem[], teacherId, lessonId, assetMap)
      }
    }
  }

  private async resolveAssetPath(
    item: ContentItem,
    teacherId: string,
    lessonId: string,
    assetMap: Map<string, { file: File; cachedPath?: string }>
  ): Promise<string | null> {
    const rawSrc = (item as any).src || (typeof item.content === 'string' ? (item.content as string) : '')
    if (!rawSrc) return null

    const normalized = this.normalizeAssetKey(rawSrc)
    const filename = rawSrc.split(/[/\\]/).pop()?.toLowerCase() || ''

    const assetEntry =
      assetMap.get(normalized) ||
      assetMap.get(this.normalizeAssetKey(filename)) ||
      null

    if (!assetEntry) {
      return null
    }

    if (assetEntry.cachedPath) {
      return assetEntry.cachedPath
    }

    const mediaType: 'image' | 'video' = /\.(mp4|webm|ogg|mov)$/i.test(assetEntry.file.name) ? 'video' : 'image'
    const newPath = await imageExtractor.saveMediaBlob(assetEntry.file, teacherId, lessonId, {
      mediaType,
      contentType: assetEntry.file.type,
      suggestedName: assetEntry.file.name,
      lessonKey: lessonId,
      contentTypeParam: 'theory'
    })
    assetEntry.cachedPath = newPath

    return newPath
  }

  /**
   * Upload .db file và extract images
   */
  async uploadQuestionBank(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string,
    loai: 'TN' | 'DS' | 'TL',
    dbFile: File
  ): Promise<void> {
    try {
      console.log(`📤 Uploading database file: L${lop}_C${chuong}_B${bai}_${loai}`)

      // Load database - dynamic import to avoid SSR issues
      const initSqlJs = (await import('sql.js')).default
      const SQL = await initSqlJs({
        locateFile: (file: string) => `https://sql.js.org/dist/${file}`
      })

      const arrayBuffer = await dbFile.arrayBuffer()
      const db = new SQL.Database(new Uint8Array(arrayBuffer))

      // Extract image links from database
      const imageLinks = await this.extractImageLinksFromDatabase(db)
      console.log(`📸 Found ${imageLinks.length} image links in database`)

      // Download and save images
      const lessonId = `lop-${lop}-chuong-${chuong}-bai-${bai}`
      const urlMap = new Map<string, string>()

      if (imageLinks.length > 0) {
        const localPaths = await imageExtractor.downloadAndSaveMultipleMedia(
          imageLinks,
          teacherId,
          lessonId
        )
        imageLinks.forEach((url, index) => {
          urlMap.set(url, localPaths[index])
        })
      }

      // Update database with local paths
      await this.updateDatabaseWithLocalPaths(db, urlMap)

      // Save database to IndexedDB
      const dbData = db.export()
      await this.saveDatabaseToIndexedDB(
        teacherId,
        lop,
        chuong,
        bai,
        loai,
        dbData
      )

      console.log(`✅ Database uploaded and processed successfully`)
    } catch (error) {
      console.error('❌ Error uploading database:', error)
      throw error
    }
  }

  /**
   * Extract image links from database
   * Hỗ trợ cả HTTP URLs và relative paths (bank/Images/...)
   */
  private async extractImageLinksFromDatabase(db: any): Promise<string[]> {
    const imageLinks = new Set<string>()

    try {
      // Get all table names
      const tables = db.exec("SELECT name FROM sqlite_master WHERE type='table'")

      if (tables.length === 0) {
        return []
      }

      // For each table, check for image fields
      for (const table of tables) {
        const tableName = table.values[0][0]

        // Get column names
        const columns = db.exec(`PRAGMA table_info(${tableName})`)
        const columnNames = columns[0].values.map((row: any[]) => row[1])

        // Check for image-related columns
        const imageColumns = columnNames.filter((col: string) =>
          col.toLowerCase().includes('hinh') ||
          col.toLowerCase().includes('image') ||
          col.toLowerCase().includes('anh')
        )

        // Query each image column
        for (const col of imageColumns) {
          try {
            const result = db.exec(`SELECT DISTINCT ${col} FROM ${tableName} WHERE ${col} IS NOT NULL AND ${col} != ''`)
            if (result.length > 0) {
              result[0].values.forEach((row: any[]) => {
                const value = row[0]
                if (value && typeof value === 'string') {
                  const trimmedValue = value.trim()
                  // Extract HTTP/HTTPS URLs
                  if (trimmedValue.startsWith('http://') || trimmedValue.startsWith('https://')) {
                    imageLinks.add(trimmedValue)
                  }
                  // Extract relative paths (bank/Images/..., /bank/Images/...)
                  else if (trimmedValue.includes('bank/Images/') || trimmedValue.includes('Bank/Images/') ||
                    trimmedValue.includes('/bank/Images/') || trimmedValue.includes('/Bank/Images/')) {
                    // Normalize path: đảm bảo có leading slash
                    const normalizedPath = trimmedValue.startsWith('/') ? trimmedValue : `/${trimmedValue}`
                    imageLinks.add(normalizedPath)
                    console.log(`📸 Found relative image path in database: ${trimmedValue} -> ${normalizedPath}`)
                  }
                }
              })
            }
          } catch (err) {
            console.warn(`⚠️ Error querying column ${col} in table ${tableName}:`, err)
          }
        }
      }
    } catch (error) {
      console.error('❌ Error extracting image links:', error)
    }

    console.log(`📸 Extracted ${imageLinks.size} image links from database (including relative paths)`)
    return Array.from(imageLinks)
  }

  /**
   * Update database with local paths
   * Hỗ trợ replace cả HTTP URLs và relative paths (bank/Images/...)
   */
  private async updateDatabaseWithLocalPaths(
    db: any,
    urlMap: Map<string, string>
  ): Promise<void> {
    console.log(`🔄 Updating database with ${urlMap.size} image path mappings`)

    urlMap.forEach((localPath, originalUrl) => {
      try {
        // Get all tables
        const tables = db.exec("SELECT name FROM sqlite_master WHERE type='table'")

        for (const table of tables) {
          const tableName = table.values[0][0]

          // Get column names
          const columns = db.exec(`PRAGMA table_info(${tableName})`)
          const columnNames = columns[0].values.map((row: any[]) => row[1])

          // Update image columns
          const imageColumns = columnNames.filter((col: string) =>
            col.toLowerCase().includes('hinh') ||
            col.toLowerCase().includes('image') ||
            col.toLowerCase().includes('anh')
          )

          for (const col of imageColumns) {
            try {
              // Replace exact match
              db.run(
                `UPDATE ${tableName} SET ${col} = ? WHERE ${col} = ?`,
                [localPath, originalUrl]
              )

              // ✅ QUAN TRỌNG: Nếu originalUrl là relative path (bank/Images/...), 
              // cũng replace các biến thể khác (có/không có leading slash, case variations)
              if (originalUrl.includes('bank/Images/') || originalUrl.includes('Bank/Images/')) {
                const variations = [
                  originalUrl,
                  originalUrl.startsWith('/') ? originalUrl.substring(1) : `/${originalUrl}`,
                  originalUrl.replace(/[Bb]ank/, 'bank'),
                  originalUrl.replace(/[Bb]ank/, 'Bank'),
                  originalUrl.replace(/\/[Ii]mages\//, '/Images/'),
                  originalUrl.replace(/\/[Ii]mages\//, '/images/')
                ]

                // Remove duplicates
                const uniqueVariations = [...new Set(variations)]

                for (const variation of uniqueVariations) {
                  if (variation !== originalUrl) {
                    try {
                      db.run(
                        `UPDATE ${tableName} SET ${col} = ? WHERE ${col} = ?`,
                        [localPath, variation]
                      )
                      console.log(`🔄 Replaced variation: ${variation} -> ${localPath}`)
                    } catch (err) {
                      // Ignore errors for variations
                    }
                  }
                }
              }

              console.log(`✅ Updated ${col} in ${tableName}: ${originalUrl} -> ${localPath}`)
            } catch (err) {
              console.warn(`⚠️ Error updating ${col} in ${tableName}:`, err)
            }
          }
        }
      } catch (error) {
        console.error('❌ Error updating database:', error)
      }
    })

    console.log(`✅ Database update completed`)
  }

  /**
   * Save database to IndexedDB
   */
  private async saveDatabaseToIndexedDB(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string,
    loai: string,
    dbData: Uint8Array
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('TeacherContentDB', 6) // Bump version to 3

      request.onerror = () => {
        const error = request.error
        console.error('❌ IndexedDB open error:', error)
        // Kiểm tra quota exceeded
        if (error && (error.name === 'QuotaExceededError' || error.message?.includes('quota'))) {
          reject(new Error('Không đủ dung lượng lưu trữ. Vui lòng xóa dữ liệu cũ hoặc liên hệ quản trị viên.'))
        } else {
          reject(error || new Error('Không thể mở IndexedDB'))
        }
      }

      request.onsuccess = () => {
        const db = request.result

        // Create object store if not exists (should be handled in onupgradeneeded, but check here just in case)
        if (!db.objectStoreNames.contains('databases')) {
          reject(new Error('IndexedDB store "databases" không tồn tại'))
          return
        }

        const transaction = db.transaction(['databases'], 'readwrite')
        const store = transaction.objectStore('databases')

        const id = `${teacherId}_L${lop}_C${chuong}_B${bai}_${loai}`
        const putRequest = store.put({
          id,
          teacherId,
          lop,
          chuong,
          bai,
          loai,
          data: Array.from(dbData),
          timestamp: Date.now()
        })

        putRequest.onerror = () => {
          const error = putRequest.error
          console.error('❌ IndexedDB put error:', error)
          if (error && (error.name === 'QuotaExceededError' || error.message?.includes('quota'))) {
            reject(new Error('Không đủ dung lượng lưu trữ. Vui lòng xóa dữ liệu cũ.'))
          } else {
            reject(error || new Error('Không thể lưu vào IndexedDB'))
          }
        }

        transaction.oncomplete = () => {
          console.log(`✅ Database saved to IndexedDB: ${id} (${dbData.length} bytes)`)
          resolve()
        }
        transaction.onerror = () => {
          const error = transaction.error
          console.error('❌ IndexedDB transaction error:', error)
          if (error && (error.name === 'QuotaExceededError' || error.message?.includes('quota'))) {
            reject(new Error('Không đủ dung lượng lưu trữ. Vui lòng xóa dữ liệu cũ.'))
          } else {
            reject(error || new Error('Transaction failed'))
          }
        }
      }

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result

        // Create databases store if not exists
        if (!db.objectStoreNames.contains('databases')) {
          db.createObjectStore('databases', { keyPath: 'id' })
        }

        // Create media store if not exists (sync with ImageExtractor)
        if (!db.objectStoreNames.contains('media')) {
          db.createObjectStore('media', { keyPath: 'path' })
        }
      }
    })
  }

  /**
   * List tất cả nội dung đã lưu của giáo viên (từ localStorage và WordPress API)
   */
  async listTeacherContent(teacherId: string): Promise<Array<{
    id: string
    lessonKey: string
    contentType: 'theory' | 'video' | 'simulation' | 'quiz' | 'infographic'
    title: string
    summary: string
    createdAt: string
    updatedAt: string
    version: number
    source: 'localStorage' | 'wordpress'
  }>> {
    const results: Array<any> = []

    // 1. Load từ localStorage
    try {
      const keys = Object.keys(localStorage)
      const teacherKeys = keys.filter(key =>
        key.startsWith(this.STORAGE_KEY_PREFIX) &&
        key.includes(`_${teacherId}_`)
      )

      for (const key of teacherKeys) {
        try {
          const stored = localStorage.getItem(key)
          if (stored) {
            const content: CustomContent = JSON.parse(stored)
            results.push({
              id: content.id,
              lessonKey: content.lessonId,
              contentType: content.contentType,
              title: content.contentData?.tieuDe || `Bài học ${content.lessonId}`,
              summary: content.contentData?.moTa || '',
              createdAt: content.createdAt,
              updatedAt: content.updatedAt,
              version: 1,
              source: 'localStorage' as const
            })
          }
        } catch (e) {
          console.warn('⚠️ Error parsing localStorage content:', e)
        }
      }
    } catch (error) {
      console.error('❌ Error loading from localStorage:', error)
    }

    // 2. Legacy WordPress API removed as per migration plan
    // Only return localStorage results
    console.log(`ℹ️ Loaded ${results.length} items from localStorage`)

    return results
  }

  /**
   * Verify dữ liệu đã lưu cho một bài học
   */
  async verifySavedContent(
    teacherId: string,
    lessonId: string
  ): Promise<{
    theory: boolean
    video: boolean
    simulation: boolean
    quiz: boolean
    infographic: boolean
    details: {
      theory?: { exists: boolean; updatedAt?: string; version?: number }
      video?: { exists: boolean; updatedAt?: string; version?: number }
      simulation?: { exists: boolean; updatedAt?: string; version?: number }
      quiz?: { exists: boolean; updatedAt?: string; version?: number }
      infographic?: { exists: boolean; updatedAt?: string; version?: number }
    }
  }> {
    const result = {
      theory: false,
      video: false,
      simulation: false,
      quiz: false,
      infographic: false,
      details: {} as any
    }

    const contentTypes: Array<'theory' | 'video' | 'simulation' | 'quiz' | 'infographic'> = ['theory', 'video', 'simulation', 'quiz', 'infographic']

    for (const contentType of contentTypes) {
      try {
        const content = await this.getCustomContent(teacherId, lessonId, contentType)
        if (content) {
          result[contentType] = true
          result.details[contentType] = {
            exists: true,
            updatedAt: content.updatedAt,
            version: 1 // Version info would need to come from API
          }
        } else {
          result.details[contentType] = {
            exists: false
          }
        }
      } catch (error) {
        console.error(`❌ Error verifying ${contentType}:`, error)
        result.details[contentType] = {
          exists: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }
    }

    return result
  }

  /**
   * Verify databases đã upload (từ IndexedDB)
   */
  async verifyUploadedDatabases(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string
  ): Promise<{
    TN: { exists: boolean; questionCount: number }
    DS: { exists: boolean; questionCount: number }
    TL: { exists: boolean; questionCount: number }
  }> {
    const result = {
      TN: { exists: false, questionCount: 0 },
      DS: { exists: false, questionCount: 0 },
      TL: { exists: false, questionCount: 0 }
    }

    for (const loai of ['TN', 'DS', 'TL'] as const) {
      try {
        const dbData = await this.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, loai)
        if (dbData && dbData.length > 0) {
          result[loai].exists = true
          result[loai].questionCount = await this.countQuestionsFromUploadedDatabase(teacherId, lop, chuong, bai, loai)
        }
      } catch (error) {
        console.error(`❌ Error verifying ${loai} database:`, error)
      }
    }

    return result
  }

  /**
   * Load database from IndexedDB
   */
  async loadDatabaseFromIndexedDB(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string,
    loai: 'TN' | 'DS' | 'TL'
  ): Promise<Uint8Array | null> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('TeacherContentDB', 6)

      request.onerror = () => {
        const error = request.error
        console.error('❌ IndexedDB open error when loading:', error)
        // Không reject, chỉ return null để fallback
        resolve(null)
      }

      request.onsuccess = () => {
        try {
          const db = request.result

          if (!db.objectStoreNames.contains('databases')) {
            console.warn('⚠️ IndexedDB store "databases" không tồn tại')
            resolve(null)
            return
          }

          const transaction = db.transaction(['databases'], 'readonly')
          const store = transaction.objectStore('databases')

          const id = `${teacherId}_L${lop}_C${chuong}_B${bai}_${loai}`
          const getRequest = store.get(id)

          getRequest.onsuccess = () => {
            const result = getRequest.result
            if (result && result.data) {
              try {
                const uint8Array = new Uint8Array(result.data)
                console.log(`✅ Loaded database from IndexedDB: ${id} (${uint8Array.length} bytes)`)
                resolve(uint8Array)
              } catch (parseError) {
                console.error('❌ Error parsing IndexedDB data:', parseError)
                resolve(null)
              }
            } else {
              console.log(`ℹ️ No database found in IndexedDB: ${id}`)
              resolve(null)
            }
          }

          getRequest.onerror = () => {
            console.error('❌ IndexedDB get error:', getRequest.error)
            // Không reject, chỉ return null để fallback
            resolve(null)
          }

          transaction.onerror = () => {
            console.error('❌ IndexedDB transaction error:', transaction.error)
            resolve(null)
          }
        } catch (error) {
          console.error('❌ Error in IndexedDB load:', error)
          resolve(null)
        }
      }

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains('databases')) {
          db.createObjectStore('databases', { keyPath: 'id' })
        }
      }
    })
  }

  /**
   * Đếm số câu hỏi từ database đã upload (từ IndexedDB)
   */
  async countQuestionsFromUploadedDatabase(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string,
    loai: 'TN' | 'DS' | 'TL'
  ): Promise<number> {
    try {
      const dbData = await this.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, loai)
      if (!dbData) {
        return 0
      }

      // Initialize SQL.js
      const initSqlJs = (await import('sql.js')).default
      const SQL = await initSqlJs({
        locateFile: (file: string) => `https://sql.js.org/dist/${file}`
      })

      const db = new SQL.Database(dbData)

      // Get all tables
      const tablesResult = db.exec("SELECT name FROM sqlite_master WHERE type='table'")
      const availableTables = tablesResult.length > 0
        ? tablesResult[0].values.map(row => row[0] as string)
        : []

      if (availableTables.length === 0) {
        db.close()
        return 0
      }

      // Try to find the questions table
      const possibleTableNames = [
        `questions_${loai.toLowerCase()}`,
        'questions_tn',
        'questions_ds',
        'questions_tl',
        'questions',
        'cau_hoi',
        'question',
        'quiz'
      ]

      for (const tableName of possibleTableNames) {
        if (availableTables.includes(tableName)) {
          try {
            const result = db.exec(`SELECT COUNT(*) as count FROM ${tableName}`)
            if (result.length > 0 && result[0].values.length > 0) {
              const count = result[0].values[0][0] as number
              db.close()
              return count
            }
          } catch (e) {
            continue
          }
        }
      }

      db.close()
      return 0
    } catch (error) {
      console.error(`❌ Error counting questions from ${loai} database:`, error)
      return 0
    }
  }

  /**
   * Set content preference (hệ thống hoặc cá nhân hóa)
   */
  async setContentPreference(
    teacherId: string,
    lessonId: string,
    useCustomContent: boolean
  ): Promise<void> {
    const preference: ContentPreference = {
      id: `${teacherId}_${lessonId}`,
      teacherId,
      lessonId,
      useCustomContent,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    const key = `${this.PREFERENCE_KEY_PREFIX}${preference.id}`
    localStorage.setItem(key, JSON.stringify(preference))

    console.log(`✅ Content preference saved: ${useCustomContent ? 'custom' : 'system'}`)
  }

  /**
   * Get content preference
   */
  async getContentPreference(
    teacherId: string,
    lessonId: string
  ): Promise<boolean> {
    const key = `${this.PREFERENCE_KEY_PREFIX}${teacherId}_${lessonId}`
    const stored = localStorage.getItem(key)

    if (!stored) {
      return false // Default to system content
    }

    const preference: ContentPreference = JSON.parse(stored)
    return preference.useCustomContent
  }

  /**
   * Save custom content
   */
  async saveCustomContent(
    teacherId: string,
    lessonId: string,
    contentType: 'theory' | 'video' | 'simulation' | 'quiz' | 'infographic',
    contentData: any,
    rawJson?: string
  ): Promise<void> {
    const content: CustomContent = {
      id: `${teacherId}_${lessonId}_${contentType}`,
      teacherId,
      lessonId,
      contentType,
      contentData,
      rawJson,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    // 1. Lưu vào localStorage (backup) - với error handling
    const key = `${this.STORAGE_KEY_PREFIX}${content.id}`
    try {
      localStorage.setItem(key, JSON.stringify(content))
    } catch (storageError: any) {
      console.warn('⚠️ localStorage quota exceeded, skipping localStorage backup:', storageError)
      // Trong production, vẫn tiếp tục lưu WordPress
      if (import.meta.env.PROD && storageError.name === 'QuotaExceededError') {
        console.warn('⚠️ localStorage full - chỉ lưu lên WordPress server')
      }
    }

    // 2. Lưu vào Server (Python Backend)
    try {
      const lessonKey = lessonId // Format: "L10_C1_B01" hoặc "bai-1-dao-dong-dieu-hoa"
      const title = contentData?.tieuDe || `Bài học ${lessonId}`
      const summary = contentData?.moTa || ''

      // Extract assets từ contentData
      const assets = this.extractAssetsFromContent(contentData, teacherId, lessonId)

      const token = this.getAuthToken()
      if (!token) {
        const errorMsg = 'Token has expired or not found. Vui lòng đăng nhập lại để tiếp tục upload lên server.'
        console.warn(`⚠️ ${errorMsg}`)
        throw new Error('Token has expired')
      }

      // ✅ UPDATED: Use Python backend instead of WordPress
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 seconds timeout

      try {
        const response = await fetch(`${API_BASE_URL}/teacher/assets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            teacherId,
            lessonKey,
            title,
            summary,
            contentData,
            rawJson: rawJson || JSON.stringify(contentData, null, 2),
            contentType,
            assets,
            status: 'published'
          }),
          signal: controller.signal
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
          const error = await response.json().catch(() => ({ message: `HTTP ${response.status}` }))
          console.error('❌ Error saving to server:', {
            status: response.status,
            statusText: response.statusText,
            error: error.message
          })

          // Phân biệt lỗi token expired
          const isTokenExpired = response.status === 401 ||
            error.message?.includes('Token has expired') ||
            error.message?.includes('token_expired')

          if (response.status === 413) {
            throw new Error('File quá lớn (Request Entity Too Large). Vui lòng kiểm tra lại kích thước ảnh/video hoặc cấu hình server.')
          }

          if (isTokenExpired) {
            throw new Error('Token has expired')
          }

          throw new Error(error.message || `Failed to save to server: ${response.status}`)
        }

        const result = await response.json()
        console.log('✅ Content saved to server:', result)
      } catch (fetchError: any) {
        clearTimeout(timeoutId)
        if (fetchError.name === 'AbortError') {
          console.error('❌ Server API timeout after 30s')
          throw new Error('Request timeout - vui lòng thử lại')
        }
        throw fetchError
      }
    } catch (error: any) {
      console.error('❌ Error saving to server (fallback to localStorage):', error)
      // Fallback: đã lưu localStorage ở trên
      // Trong production, vẫn throw error để user biết
      if (import.meta.env.PROD) {
        throw new Error(`Không thể lưu lên server: ${error.message || 'Unknown error'}. Nội dung đã được lưu tạm trong trình duyệt.`)
      }
    }

    console.log(`✅ Custom content saved: ${contentType}`)
  }

  /**
   * Extract assets (images/videos) from content data
   */
  private extractAssetsFromContent(contentData: any, teacherId: string, lessonId: string): any[] {
    const assets: any[] = []

    // Recursive function để tìm tất cả image/video paths
    const findMediaPaths = (obj: any) => {
      if (typeof obj !== 'object' || obj === null) return

      if (Array.isArray(obj)) {
        obj.forEach(item => findMediaPaths(item))
      } else {
        Object.keys(obj).forEach(key => {
          const value = obj[key]

          if (key === 'src' || key === 'content') {
            if (typeof value === 'string' && value.startsWith('/teacher_content/')) {
              const fileName = value.split('/').pop() || ''
              const isVideo = value.includes('/videos/')

              assets.push({
                fileName,
                path: value,
                mimeType: isVideo ? 'video/mp4' : 'image/png',
                fileSize: 0 // Có thể tính từ blob nếu cần
              })
            }
          } else if (typeof value === 'object') {
            findMediaPaths(value)
          }
        })
      }
    }

    findMediaPaths(contentData)

    // Remove duplicates
    const uniqueAssets = assets.filter((asset, index, self) =>
      index === self.findIndex(a => a.path === asset.path)
    )

    return uniqueAssets
  }

  /**
   * ✅ NEW: Get all content types in one API call
   * Returns all content types (theory, video, simulation, quiz, infographic) in a single request
   * Updated to fetch from Python Backend (load-from-disk)
   */
  async getAllContent(
    teacherId: string,
    lessonId: string,
    forceFromServer: boolean = false
  ): Promise<{
    theory: CustomContent | null
    video: CustomContent | null
    simulation: CustomContent | null
    quiz: CustomContent | null
    infographic: CustomContent | null
  }> {
    const result = {
      theory: null as CustomContent | null,
      video: null as CustomContent | null,
      simulation: null as CustomContent | null,
      quiz: null as CustomContent | null,
      infographic: null as CustomContent | null
    }

    // 1. Try load from localStorage first (only if not forcing from server)
    if (!forceFromServer) {
      const contentTypes: Array<'theory' | 'video' | 'simulation' | 'quiz' | 'infographic'> = ['theory', 'video', 'simulation', 'quiz', 'infographic']
      for (const contentType of contentTypes) {
        const key = `${this.STORAGE_KEY_PREFIX}${teacherId}_${lessonId}_${contentType}`
        const stored = localStorage.getItem(key)
        if (stored) {
          try {
            result[contentType] = JSON.parse(stored) as CustomContent
          } catch (e) {
            console.warn(`⚠️ Error parsing cached ${contentType}:`, e)
          }
        }
      }

      // If key content types are cached, might consider returning early
      // But let's check broadly
      if (result.theory || result.video || result.simulation || result.quiz || result.infographic) {
        console.log('✅ Loaded content from localStorage cache')
        return result
      }
    }

    // 2. Load from Python Backend API (single call)
    try {
      const token = this.getAuthToken()
      const lessonKey = lessonId
      // ✅ SWITCH to Python Backend URL where save-json writes to
      const API_HOST = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      console.log(`🔍 Loading all content from server (Python Backend) for teacher ${teacherId}, lesson ${lessonKey}${forceFromServer ? ' (FORCED)' : ''}`)

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 seconds timeout

      try {
        // Build headers - Authenticated request preferred but handle public if needed
        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        }
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        const response = await fetch(
          `${API_HOST}/teacher/content/load-from-disk?teacherId=${teacherId}&lessonKey=${encodeURIComponent(lessonKey)}`,
          {
            headers,
            signal: controller.signal
          }
        )

        clearTimeout(timeoutId)

        if (response.ok) {
          const data = await response.json()
          console.log('✅ Loaded all content from server:', {
            hasTheory: !!data.theory,
            hasVideo: !!data.video,
            hasSimulation: !!data.simulation,
            hasQuiz: !!data.quiz,
            hasInfographic: !!data.infographic
          })

          // Convert API response to CustomContent format
          const contentTypes: Array<'theory' | 'video' | 'simulation' | 'quiz' | 'infographic'> = ['theory', 'video', 'simulation', 'quiz', 'infographic']
          for (const contentType of contentTypes) {
            if (data[contentType]) {
              const customContent: CustomContent = {
                id: `${teacherId}_${lessonId}_${contentType}`,
                teacherId,
                lessonId,
                contentType,
                contentData: data[contentType],
                rawJson: JSON.stringify(data[contentType], null, 2),
                createdAt: new Date().toISOString(), // Backend doesn't return file dates yet
                updatedAt: new Date().toISOString()
              }
              result[contentType] = customContent

              // Cache to localStorage
              try {
                const key = `${this.STORAGE_KEY_PREFIX}${teacherId}_${lessonId}_${contentType}`
                localStorage.setItem(key, JSON.stringify(customContent))
                console.log(`💾 Cached ${contentType} to localStorage`)
              } catch (storageError: any) {
                console.warn(`⚠️ localStorage quota exceeded for ${contentType}:`, storageError)
              }
            }
          }
        } else if (response.status === 404) {
          console.log('ℹ️ No teacher content found (404)')
        } else {
          console.warn(`⚠️ API returned ${response.status}`)
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId)
        if (fetchError.name === 'AbortError') {
          console.warn('⚠️ API timeout, using localStorage only')
        } else {
          console.warn('⚠️ Error loading from API, using localStorage only:', fetchError)
        }
      }
    } catch (error) {
      console.warn('⚠️ Error loading from API, using localStorage only:', error)
    }

    return result
  }

  /**
   * Get custom content (single content type - kept for backward compatibility)
   */
  async getCustomContent(
    teacherId: string,
    lessonId: string,
    contentType: 'theory' | 'video' | 'simulation' | 'quiz' | 'infographic',
    forceFromServer: boolean = false
  ): Promise<CustomContent | null> {
    const key = `${this.STORAGE_KEY_PREFIX}${teacherId}_${lessonId}_${contentType}`

    // 1. Load from localStorage if not forced
    if (!forceFromServer) {
      const stored = localStorage.getItem(key)
      if (stored) return JSON.parse(stored) as CustomContent
    }

    // 2. Fetch from Python Backend (Static Files)
    try {
      const match = lessonId.match(/lop-(\d+)-chuong-(\d+)-bai-(\d+)/) || lessonId.match(/lop-(\d+)-chuong-(\d+)-bai-(.+)/)
      if (!match) {
        console.warn(`[getCustomContent] Cannot parse lessonId: ${lessonId}`)
        return null
      }

      const [_, lop, chuong, bai] = match
      // Format bai number to 2 digits for folder structure (B01, B10)
      // Check if 'bai' is numeric
      const baiNum = parseInt(bai, 10)
      const baiFormatted = !isNaN(baiNum) ? `B${baiNum.toString().padStart(2, '0')}` : `B${bai}`

      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      const basePath = `/teacher_content/${teacherId}/L${lop}/C${chuong}/${baiFormatted}`

      let filename = 'metadata.json'
      let jsonKey: string | null = null // Key inside JSON to return, if specific

      if (contentType === 'theory') {
        filename = 'noi-dung.json'
      } else if (contentType === 'quiz') {
        // For quiz, check existence of any quiz file or prefer TN.
        // Returning TN by default for verifying existence.
        filename = 'quiz-TN.json'
        // Note: Real quiz loading usually happens via separate DB loading methods.
        // This is mostly for 'verifySavedContent'.
      } else if (['video', 'simulation', 'infographic'].includes(contentType)) {
        filename = 'metadata.json'
      }

      const url = `${API_BASE_URL}${basePath}/${filename}`
      console.log(`🔍 Fetching ${contentType} from ${url}`)

      const response = await fetch(url)
      if (response.ok) {
        const data = await response.json()

        let contentData = data
        // For metadata, we might want to filter or just return the whole object
        // For now return whole object as contentData
        if (contentType === 'quiz' && !data) {
          // Try DS if TN missing? No, failing is fine for now.
        }

        const customContent: CustomContent = {
          id: `${teacherId}_${lessonId}_${contentType}`,
          teacherId,
          lessonId,
          contentType,
          contentData,
          rawJson: JSON.stringify(contentData),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        // Cache to localStorage
        try {
          localStorage.setItem(key, JSON.stringify(customContent))
        } catch (e) { /* ignore quota */ }

        console.log(`✅ Loaded ${contentType} from server`)
        return customContent
      } else {
        // If 404, it just means content doesn't exist on server
        if (response.status !== 404) {
          console.warn(`⚠️ Error fetching ${url}: ${response.status}`)
        }
      }

    } catch (error) {
      console.error(`❌ Error in getCustomContent:`, error)
    }

    return null
  }

  /**
   * Generate share link với teacherId (legacy - dùng URL params)
   */
  generateShareLink(
    teacherId: string,
    lop: number,
    chuong: number,
    bai: string
  ): string {
    // Extract bai number if it's in format "bai-1-..."
    const baiNumber = bai.replace('bai-', '').split('-')[0]
    return `/lop/${lop}/chuong/${chuong}/bai/${bai}?teacher=${teacherId}`
  }

  /**
   * Tạo share link token mới
   */
  async createShareLink(
    teacherId: string,
    lessonKey: string,
    options?: {
      expiresAt?: string | null
      maxUses?: number
    }
  ): Promise<{ token: string; shareUrl: string }> {
    try {
      // 1. Lấy content_id từ database
      const token = this.getAuthToken()
      if (!token) {
        throw new Error('No auth token found')
      }

      const contentResponse = await fetch(
        `${this.API_BASE_URL}/teacher-content?teacherId=${teacherId}&lessonKey=${lessonKey}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      if (!contentResponse.ok) {
        throw new Error('Content not found. Please upload content first.')
      }

      const contentData = await contentResponse.json()
      const contentId = contentData.id

      // 2. Tạo share link
      const shareResponse = await fetch(
        `${this.API_BASE_URL}/teacher_content/share-link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            contentId,
            expiresAt: options?.expiresAt || null,
            maxUses: options?.maxUses ?? 0
          })
        }
      )

      if (!shareResponse.ok) {
        const error = await shareResponse.json().catch(() => ({ message: 'Unknown error' }))
        throw new Error(error.message || 'Failed to create share link')
      }

      const shareData = await shareResponse.json()
      const shareToken = shareData.token
      const shareUrl = `${window.location.origin}/share/${shareToken}`

      console.log('✅ Share link created:', shareUrl)
      return { token: shareToken, shareUrl }
    } catch (error: any) {
      console.error('❌ Error creating share link:', error)
      throw error
    }
  }

  /**
   * Lấy content qua share token (public access)
   */
  async getContentByShareToken(token: string): Promise<any> {
    try {
      const response = await fetch(
        `${this.API_BASE_URL}/teacher_content/share-link/${token}`
      )

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }))
        throw new Error(error.message || 'Failed to load shared content')
      }

      const data = await response.json()

      // Parse JSON content
      const contentData = typeof data.json_content === 'string'
        ? JSON.parse(data.json_content)
        : data.json_content

      return {
        ...data,
        contentData
      }
    } catch (error: any) {
      console.error('❌ Error loading shared content:', error)
      throw error
    }
  }

  /**
   * Liệt kê tất cả share links của giáo viên
   */
  async listShareLinks(teacherId: string): Promise<any[]> {
    try {
      const token = this.getAuthToken()
      if (!token) {
        throw new Error('No auth token found')
      }

      const response = await fetch(
        `${this.API_BASE_URL}/teacher_content/share-link?teacherId=${teacherId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }))
        throw new Error(error.message || 'Failed to list share links')
      }

      const data = await response.json()
      return Array.isArray(data) ? data : []
    } catch (error: any) {
      console.error('❌ Error listing share links:', error)
      throw error
    }
  }

  /**
   * Load lesson content theo preference
   */
  async loadLessonContent(
    lessonId: string,
    teacherId?: string
  ): Promise<LessonData | null> {
    // Nếu có teacherId, check preference
    if (teacherId) {
      const useCustom = await this.getContentPreference(teacherId, lessonId)

      if (useCustom) {
        const customContent = await this.getCustomContent(teacherId, lessonId, 'theory')
        if (customContent) {
          return customContent.contentData as LessonData
        }
      }
    }

    // Return null để load từ hệ thống (ContentServiceSimple)
    return null
  }
  /**
   * ✅ NEW: Upload database file to WordPress server
   * Upload quiz database (TN/DS/TL) to WordPress as binary file
   */
  async uploadDatabaseToWordPress(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string,
    loai: 'TN' | 'DS' | 'TL',
    dbData: Uint8Array
  ): Promise<void> {
    try {
      console.log(`📤 Uploading ${loai} database to WordPress server...`)

      const token = this.getAuthToken()
      if (!token) {
        throw new Error('No auth token found')
      }

      // Create FormData with database file
      const formData = new FormData()
      const blob = new Blob([dbData as any], { type: 'application/x-sqlite3' })
      const filename = `L${lop}_C${chuong}_B${bai}_${loai}.db`
      formData.append('database', blob, filename)
      formData.append('teacher_id', teacherId)
      formData.append('lop', lop)
      formData.append('chuong', chuong)
      formData.append('bai', bai)
      formData.append('type', loai)

      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      const response = await fetch(
        `${API_BASE_URL}/teacher/content/upload-database`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // Don't set Content-Type - browser will set it with boundary for FormData
          },
          body: formData
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Upload failed: ${response.status} - ${errorText}`)
      }

      const result = await response.json()
      console.log(`✅ Database uploaded successfully:`, result)
    } catch (error) {
      console.error(`❌ Error uploading ${loai} database:`, error)
      throw error
    }
  }

  /**
   * ✅ NEW: Get teacher databases for student
   * Downloads .db files from server and returns as Uint8Array map
   */
  async getTeacherDatabases(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string,
    timeout: number = 5000 // ✅ FIX: Default timeout 5 giây
  ): Promise<Record<'TN' | 'DS' | 'TL', Uint8Array | null>> {
    const results: Record<'TN' | 'DS' | 'TL', Uint8Array | null> = { TN: null, DS: null, TL: null }

    try {
      // ✅ FIX: Extract số từ lop, chuong, bai để đảm bảo format đúng
      // lop có thể là "11" hoặc "lop-11"
      let lopNum = lop
      const lopMatch = lop.match(/(\d+)/)
      if (lopMatch) {
        lopNum = lopMatch[1]
      }

      // chuong có thể là "1" hoặc "chuong-1"
      let chuongNum = chuong
      const chuongMatch = chuong.match(/(\d+)/)
      if (chuongMatch) {
        chuongNum = chuongMatch[1]
      }

      // bai đã được extract đúng từ XemBaiHoc, nhưng đảm bảo chỉ lấy số
      let baiNum = bai
      const baiMatch = bai.match(/(\d+)/)
      if (baiMatch) {
        baiNum = baiMatch[1]
      }

      // Construct base URL for this lesson's assets
      // Format: /teacher_content/{teacherId}/L{lop}/C{chuong}/B{bai}
      // ✅ FIX: Pad số bài với 0 (1 -> 01, 10 -> 10)
      const baiPadded = baiNum.padStart(2, '0')
      const baseUrl = `${import.meta.env.VITE_WP_UPLOADS_URL || 'https://www.vatli365.vn/wp-content/uploads'}/teacher_content/${teacherId}/L${lopNum}/C${chuongNum}/B${baiPadded}`

      console.log(`📥 Fetching teacher databases from: ${baseUrl}`)
      console.log(`📋 Parameters (RAW → EXTRACTED):`, {
        rawLop: lop,
        rawChuong: chuong,
        rawBai: bai,
        extractedLop: lopNum,
        extractedChuong: chuongNum,
        extractedBai: baiNum,
        baiPadded,
        teacherId,
        fullPath: baseUrl
      })

      const types = ['TN', 'DS', 'TL'] as const

      // ✅ FIX: Sử dụng API endpoint thay vì fetch trực tiếp để tránh CORS
      // ✅ FIX: Đảm bảo luôn thử load từ server trước (không bỏ qua nếu có teacherId)
      // ✅ FIX: Thêm timeout để tránh treo
      await Promise.all(types.map(async (type) => {
        try {
          // Sử dụng WordPress API endpoint thay vì direct file URL
          // ✅ FIX: Đảm bảo đường dẫn đúng: /teacher_content/database/{teacherId}/{lop}/{chuong}/{bai}/{type}
          const apiUrl = `${this.API_BASE_URL}/teacher_content/database/${teacherId}/${lopNum}/${chuongNum}/${baiNum}/${type}?t=${Date.now()}`
          console.log(`🔍 [Phần 4 - Database] Attempting to fetch ${type} database from API: ${apiUrl}`)
          console.log(`🔍 [Phần 4 - Database] Parameters:`, {
            teacherId,
            lopNum,
            chuongNum,
            baiNum,
            type,
            expectedPath: `teacher-content/${teacherId}/L${lopNum}/C${chuongNum}/B${baiNum.padStart(2, '0')}/${type}.db`
          })

          // ✅ FIX: Thêm timeout để tránh treo
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), timeout)

          try {
            const response = await fetch(apiUrl, {
              method: 'GET',
              cache: 'no-cache',
              headers: {
                'Accept': 'application/json'
              },
              signal: controller.signal
            })

            clearTimeout(timeoutId)

            console.log(`📊 [Phần 4 - Database] ${type} database API response:`, {
              status: response.status,
              statusText: response.statusText,
              ok: response.ok,
              contentType: response.headers.get('content-type')
            })

            if (response.ok) {
              // ✅ FIX: API trả về JSON với base64 encoded data
              const jsonData = await response.json()
              if (jsonData.success && jsonData.data) {
                // Decode base64 to Uint8Array
                const binaryString = atob(jsonData.data)
                const bytes = new Uint8Array(binaryString.length)
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i)
                }
                results[type] = bytes
                console.log(`✅ [Phần 4 - Database] Downloaded ${type} database via API: ${bytes.length} bytes`)
              } else {
                console.warn(`⚠️ [Phần 4 - Database] ${type} database API response invalid:`, jsonData)
              }
            } else if (response.status === 404) {
              console.log(`⚠️ [Phần 4 - Database] ${type} database not found (404) via API`)
              console.log(`⚠️ [Phần 4 - Database] Expected path: teacher-content/${teacherId}/L${lopNum}/C${chuongNum}/B${baiNum.padStart(2, '0')}/${type}.db`)
            } else {
              const errorText = await response.text().catch(() => 'Unknown error')
              console.warn(`⚠️ [Phần 4 - Database] ${type} database API fetch failed: ${response.status} ${response.statusText}`, errorText)
            }
          } catch (fetchError: any) {
            clearTimeout(timeoutId)
            if (fetchError.name === 'AbortError') {
              console.warn(`⏱️ [Phần 4 - Database] ${type} database fetch timeout after ${timeout}ms`)
            } else {
              console.warn(`❌ [Phần 4 - Database] Error fetching ${type} database via API:`, fetchError)
            }
            // Continue với các database khác
          }
        } catch (error) {
          console.warn(`❌ [Phần 4 - Database] Error fetching ${type} database via API:`, error)
          // Continue với các database khác
        }
      }))

      console.log(`📊 Final database results:`, {
        TN: results.TN ? `${results.TN.length} bytes` : 'null',
        DS: results.DS ? `${results.DS.length} bytes` : 'null',
        TL: results.TL ? `${results.TL.length} bytes` : 'null'
      })

    } catch (error) {
      console.error('❌ Error in getTeacherDatabases:', error)
    }

    return results
  }

  /**
   * ✅ NEW: Upload quiz from .json file (replaces .db format)
   * Parse JSON, extract images, save locally
   */
  async uploadQuizJson(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string,
    loai: 'TN' | 'DS' | 'TL',
    jsonFile: File
  ): Promise<void> {
    try {
      console.log(`📤 Uploading JSON quiz file: L${lop}_C${chuong}_B${bai}_${loai}`)

      // 1. Parse JSON file
      const text = await jsonFile.text()
      let quizData: any

      try {
        quizData = JSON.parse(text)
      } catch (parseError) {
        throw new Error('Invalid JSON format. Please check your file.')
      }

      // ✅ FIX: Auto-wrap and normalize quiz data format
      // Support multiple formats:
      // 1. Array: [{id, question, ...}, ...]
      // 2. Object with "questions": {questions: [...]}
      // 3. Object with type-specific key: {questions_tn: [...]} or {questions_ds: [...]} or {questions_tl: [...]}
      if (Array.isArray(quizData)) {
        console.log(`📦 [QUIZ UPLOAD] Detected array format, wrapping into {questions: [...]}`)
        quizData = { questions: quizData }
      } else if (quizData.questions_tn) {
        console.log(`📦 [QUIZ UPLOAD] Detected {questions_tn: [...]} format, normalizing...`)
        quizData = { questions: quizData.questions_tn }
      } else if (quizData.questions_ds) {
        console.log(`📦 [QUIZ UPLOAD] Detected {questions_ds: [...]} format, normalizing...`)
        quizData = { questions: quizData.questions_ds }
      } else if (quizData.questions_tl) {
        console.log(`📦 [QUIZ UPLOAD] Detected {questions_tl: [...]} format, normalizing...`)
        quizData = { questions: quizData.questions_tl }
      }

      // 2. Validate JSON structure
      if (!quizData.questions || !Array.isArray(quizData.questions)) {
        console.error('❌ [QUIZ UPLOAD] Invalid structure:', quizData)
        throw new Error('Invalid quiz JSON: missing "questions" array')
      }

      console.log(`📋 Found ${quizData.questions.length} questions in JSON`)

      // 3. Extract image URLs from questions
      const imageUrls = this.extractImageUrlsFromQuizJson(quizData)
      console.log(`📸 Found ${imageUrls.length} image URLs in quiz`)

      // 4. Download and save images locally
      const lessonId = `lop-${lop}-chuong-${chuong}-bai-${bai}`
      const urlMap = new Map<string, string>()

      if (imageUrls.length > 0) {
        const localPaths = await imageExtractor.downloadAndSaveMultipleMedia(
          imageUrls,
          teacherId,
          lessonId
        )
        imageUrls.forEach((url, index) => {
          urlMap.set(url, localPaths[index])
        })
      }

      // 5. Replace image URLs with local paths in quiz data
      this.replaceImageUrlsInQuizJson(quizData, urlMap)

      // 6. Save JSON to IndexedDB
      await this.saveQuizJsonToIndexedDB(
        teacherId,
        lop,
        chuong,
        bai,
        loai,
        quizData
      )

      // 7. Save JSON to Server ✅
      await this.saveQuizJsonToServer(
        teacherId,
        lessonId,
        loai,
        quizData
      )

      console.log(`✅ JSON quiz uploaded and processed successfully`)
    } catch (error) {
      console.error('❌ Error uploading JSON quiz:', error)
      throw error
    }
  }

  /**
   * Extract image URLs from quiz JSON
   */
  private extractImageUrlsFromQuizJson(quizData: any): string[] {
    const imageUrls = new Set<string>()

    if (!quizData.questions) return []

    for (const question of quizData.questions) {
      // ✅ FIX: Check question_image_link (field from B06 format)
      if (question.question_image_link && typeof question.question_image_link === 'string') {
        const url = question.question_image_link.trim()
        if (url && url.length > 0) {
          // Support: bank/Images/..., /bank/Images/..., http://..., https://...
          imageUrls.add(url)
        }
      }

      // ✅ FIX: Check solution_image_link (field from B06 format)
      if (question.solution_image_link && typeof question.solution_image_link === 'string') {
        const url = question.solution_image_link.trim()
        if (url && url.length > 0) {
          imageUrls.add(url)
        }
      }

      // Backward compatibility: Check old "image" field
      if (question.image && typeof question.image === 'string') {
        const url = question.image.trim()
        if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/bank/') || url.startsWith('bank/')) {
          imageUrls.add(url)
        }
      }

      // Check answer images (if any)
      if (question.answers && Array.isArray(question.answers)) {
        for (const answer of question.answers) {
          if (typeof answer === 'object' && answer.image) {
            const url = answer.image.trim()
            if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/bank/') || url.startsWith('bank/')) {
              imageUrls.add(url)
            }
          }
        }
      }

      // Check explanation image (if any)
      if (question.explanation && typeof question.explanation === 'object' && question.explanation.image) {
        const url = question.explanation.image.trim()
        if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/bank/') || url.startsWith('bank/')) {
          imageUrls.add(url)
        }
      }
    }

    const urls = Array.from(imageUrls)
    console.log(`📸 [EXTRACT IMAGES] Found ${urls.length} unique image URLs:`, urls)
    return urls
  }

  /**
   * Replace image URLs with local paths in quiz JSON
   */
  private replaceImageUrlsInQuizJson(quizData: any, urlMap: Map<string, string>): void {
    if (!quizData.questions) return

    for (const question of quizData.questions) {
      // ✅ FIX: Replace question_image_link (B06 format)
      if (question.question_image_link && urlMap.has(question.question_image_link)) {
        const newPath = urlMap.get(question.question_image_link)!
        console.log(`📸 [REPLACE] ${question.question_image_link} → ${newPath}`)
        question.question_image_link = newPath
      }

      // ✅ FIX: Replace solution_image_link (B06 format)
      if (question.solution_image_link && urlMap.has(question.solution_image_link)) {
        const newPath = urlMap.get(question.solution_image_link)!
        console.log(`📸 [REPLACE] ${question.solution_image_link} → ${newPath}`)
        question.solution_image_link = newPath
      }

      // Backward compatibility: Replace old "image" field
      if (question.image && urlMap.has(question.image)) {
        question.image = urlMap.get(question.image)
      }

      // Replace answer images
      if (question.answers && Array.isArray(question.answers)) {
        for (const answer of question.answers) {
          if (typeof answer === 'object' && answer.image && urlMap.has(answer.image)) {
            answer.image = urlMap.get(answer.image)
          }
        }
      }

      // Replace explanation image
      if (question.explanation && typeof question.explanation === 'object' && question.explanation.image && urlMap.has(question.explanation.image)) {
        question.explanation.image = urlMap.get(question.explanation.image)
      }
    }

    console.log(`✅ [REPLACE] Replaced ${urlMap.size} image URLs in quiz JSON`)
  }

  /**
   * Save quiz JSON to IndexedDB
   */
  private async saveQuizJsonToIndexedDB(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string,
    loai: string,
    quizData: any
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('TeacherContentDB', 6)

      request.onerror = () => {
        reject(request.error || new Error('Cannot open IndexedDB'))
      }

      request.onsuccess = () => {
        const db = request.result

        if (!db.objectStoreNames.contains('quizzes')) {
          // Create store if not exists
          const version = db.version + 1
          db.close()
          const upgradeRequest = indexedDB.open('TeacherContentDB', version)

          upgradeRequest.onupgradeneeded = (event: any) => {
            const upgradedDb = event.target.result
            if (!upgradedDb.objectStoreNames.contains('quizzes')) {
              upgradedDb.createObjectStore('quizzes', { keyPath: 'id' })
            }
          }

          upgradeRequest.onsuccess = () => {
            // Retry save after upgrade
            this.saveQuizJsonToIndexedDB(teacherId, lop, chuong, bai, loai, quizData)
              .then(resolve)
              .catch(reject)
          }

          upgradeRequest.onerror = () => {
            reject(upgradeRequest.error || new Error('Cannot upgrade IndexedDB'))
          }
          return
        }

        const transaction = db.transaction(['quizzes'], 'readwrite')
        const store = transaction.objectStore('quizzes')

        const id = `${teacherId}_L${lop}_C${chuong}_B${bai}_${loai}_json`
        const putRequest = store.put({
          id,
          teacherId,
          lop,
          chuong,
          bai,
          loai,
          format: 'json',
          data: quizData,
          timestamp: Date.now()
        })

        putRequest.onerror = () => {
          reject(putRequest.error || new Error('Cannot save to IndexedDB'))
        }

        transaction.oncomplete = () => {
          console.log(`✅ Quiz JSON saved to IndexedDB: ${id}`)
          resolve()
        }

        transaction.onerror = () => {
          reject(transaction.error || new Error('Transaction failed'))
        }
      }

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains('quizzes')) {
          db.createObjectStore('quizzes', { keyPath: 'id' })
        }
        // Ensure databases store exists (legacy/sync)
        if (!db.objectStoreNames.contains('databases')) {
          db.createObjectStore('databases', { keyPath: 'id' })
        }
        // Ensure media store exists (sync with ImageExtractor)
        if (!db.objectStoreNames.contains('media')) {
          db.createObjectStore('media', { keyPath: 'path' })
        }
      }
    })
  }

  /**
   * ✅ NEW: Upload infographic image
   */
  async uploadInfographic(
    teacherId: string,
    lessonId: string,
    imageFile: File
  ): Promise<string> {
    try {
      console.log(`📤 Uploading infographic: ${imageFile.name}`)

      // 1. Validate image format
      if (!imageFile.type.startsWith('image/')) {
        throw new Error('File must be an image (PNG, JPG, WebP)')
      }

      // 2. Compress if too large (>2MB)
      let processedFile = imageFile
      if (imageFile.size > 2 * 1024 * 1024) {
        console.log(`📦 Compressing large image (${(imageFile.size / 1024 / 1024).toFixed(2)}MB)`)
        processedFile = await this.compressImage(imageFile)
      }

      // 3. Save using ImageExtractor
      // ✅ FIX: Preserve original file extension
      const fileExt = imageFile.name.split('.').pop() || 'png'
      const path = await imageExtractor.saveMediaBlob(
        processedFile,
        teacherId,
        lessonId,
        {
          mediaType: 'image',
          contentType: processedFile.type,
          suggestedName: `infographic.${fileExt}`,  // ✅ Keep extension
          lessonKey: lessonId,
          contentTypeParam: 'infographic'
        }
      )

      // 4. Save metadata to localStorage
      const customContent: CustomContent = {
        id: `${teacherId}_${lessonId}_infographic`,
        teacherId,
        lessonId,
        contentType: 'infographic',
        contentData: {
          imagePath: path,
          originalName: imageFile.name,
          size: processedFile.size,
          type: processedFile.type
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      const key = `${this.STORAGE_KEY_PREFIX}${teacherId}_${lessonId}_infographic`
      localStorage.setItem(key, JSON.stringify(customContent))

      console.log(`✅ Infographic uploaded successfully: ${path}`)
      return path
    } catch (error) {
      console.error('❌ Error uploading infographic:', error)
      throw error
    }
  }

  /**
   * Compress image using canvas (simple compression)
   */
  private async compressImage(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          if (!ctx) {
            reject(new Error('Cannot get canvas context'))
            return
          }

          // Max dimensions
          const MAX_WIDTH = 1920
          const MAX_HEIGHT = 1920

          let width = img.width
          let height = img.height

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width
              width = MAX_WIDTH
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height
              height = MAX_HEIGHT
            }
          }

          canvas.width = width
          canvas.height = height
          ctx.drawImage(img, 0, 0, width, height)

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'))
                return
              }
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              })
              console.log(`✅ Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`)
              resolve(compressedFile)
            },
            file.type,
            0.85 // Quality
          )
        }
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = e.target?.result as string
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }

  /**
   * ✅ UPDATED: Get teacher content for student (fetches from server)
   * This is the main entry point for students to access teacher-customized content
   * 
   * Priority:
   * 1. Teacher's custom content from server (if teacher linked and content exists)
   * 2. System default content (fallback)
   */
  async getTeacherContentForStudent(
    lessonId: string,
    contentType: 'theory' | 'video' | 'simulation' | 'quiz' | 'infographic',
    teacherId?: string  // Optional: can be passed from URL or context
  ): Promise<CustomContent | null> {
    try {
      // 1. Get linked teacher ID (from URL param or localStorage)
      const linkedTeacherId = teacherId || localStorage.getItem('linked_teacher_id')

      if (!linkedTeacherId) {
        console.log('ℹ️ No teacher linked, using system default content')
        return null // Will trigger fallback to system content
      }

      console.log(`🔍 Fetching custom content from teacher ${linkedTeacherId} for lesson ${lessonId}`)

      // 2. Fetch from server based on content type
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      // Build path: /teacher_content/{teacherId}/L{lop}/C{chuong}/B{bai}/
      const lessonInfo = this.parseLessonKey(lessonId)
      if (!lessonInfo) {
        console.warn(`⚠️ Could not parse lesson key: ${lessonId}`)
        return null
      }

      const { lop, chuong, bai } = lessonInfo
      const baiPadded = bai.padStart(2, '0')
      const basePath = `/teacher_content/${linkedTeacherId}/L${lop}/C${chuong}/B${baiPadded}`

      // 3. Fetch appropriate content based on type
      switch (contentType) {
        case 'theory': {
          const theoryUrl = `${API_BASE_URL}${basePath}/noi-dung.json`
          const response = await fetch(theoryUrl)
          if (response.ok) {
            const theoryData = await response.json()
            console.log('✅ Loaded custom theory from server')
            return {
              id: `${linkedTeacherId}_${lessonId}_theory`,
              teacherId: linkedTeacherId,
              lessonId,
              contentType: 'theory',
              contentData: theoryData,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            } as CustomContent
          }
          break
        }

        case 'quiz': {
          const quizData: any = {}
          for (const type of ['TN', 'DS', 'TL']) {
            const quizUrl = `${API_BASE_URL}${basePath}/quiz-${type}.json`
            try {
              const response = await fetch(quizUrl)
              if (response.ok) {
                quizData[type] = await response.json()
              }
            } catch (e) { }
          }
          if (Object.keys(quizData).length > 0) {
            console.log('✅ Loaded custom quiz from server')
            return {
              id: `${linkedTeacherId}_${lessonId}_quiz`,
              teacherId: linkedTeacherId,
              lessonId,
              contentType: 'quiz',
              contentData: quizData,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            } as CustomContent
          }
          break
        }
      }

      console.log(`ℹ️ No custom ${contentType} found`)
      return null

    } catch (error) {
      console.error(`❌ Error fetching teacher content:`, error)
      return null
    }
  }

  /**
   * ✅ NEW: Set linked teacher ID (called when student accesses via share link)
   */
  setLinkedTeacherId(teacherId: string): void {
    localStorage.setItem('linked_teacher_id', teacherId)
    console.log(`✅ Linked student to teacher: ${teacherId}`)
  }

  /**
   * ✅ NEW: Get current linked teacher ID
   */
  getLinkedTeacherId(): string | null {
    return localStorage.getItem('linked_teacher_id')
  }

  /**
   * ✅ NEW: Clear teacher link (for testing or unlinking)
   */
  clearLinkedTeacher(): void {
    localStorage.removeItem('linked_teacher_id')
    console.log(`✅ Cleared teacher link`)
  }

  /**
   * ✅ NEW: Save theory JSON to server
   */
  async saveTheoryJson(teacherId: string, lessonId: string, jsonData: any): Promise<void> {
    try {
      const token = this.getAuthToken()
      if (!token) {
        console.warn('⚠️ No auth token, skipping server save')
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      const response = await fetch(`${API_BASE_URL}/teacher/content/save-json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          teacherId,
          lessonKey: lessonId,
          contentType: 'theory',
          jsonData
        })
      })

      if (response.ok) {
        const result = await response.json()
        console.log(`✅ Theory JSON saved to server: ${result.path}`)
      } else {
        console.error(`❌ Failed to save theory JSON: ${response.status}`)
      }
    } catch (error) {
      console.error('❌ Error saving theory JSON:', error)
    }
  }

  /**
   * ✅ NEW: Save quiz JSON to server
   */
  async saveQuizJsonToServer(
    teacherId: string,
    lessonId: string,
    type: 'TN' | 'DS' | 'TL',
    jsonData: any
  ): Promise<void> {
    try {
      const token = this.getAuthToken()
      if (!token) {
        console.warn('⚠️ No auth token, skipping server save')
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      const response = await fetch(`${API_BASE_URL}/teacher/content/save-json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          teacherId,
          lessonKey: lessonId,
          contentType: `quiz-${type}`,
          jsonData
        })
      })

      if (response.ok) {
        const result = await response.json()
        console.log(`✅ Quiz ${type} JSON saved to server: ${result.path}`)
      } else {
        console.error(`❌ Failed to save quiz ${type} JSON: ${response.status}`)
      }
    } catch (error) {
      console.error(`❌ Error saving quiz ${type} JSON:`, error)
    }
  }

  /**
   * ✅ NEW: Save metadata to server
   */
  async saveMetadata(teacherId: string, lessonId: string, metadata: any): Promise<void> {
    try {
      const token = this.getAuthToken()
      if (!token) {
        console.warn('⚠ No auth token, skipping server save')
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      const response = await fetch(`${API_BASE_URL}/teacher/content/save-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          teacherId,
          lessonKey: lessonId,
          metadata
        })
      })

      if (response.ok) {
        const result = await response.json()
        console.log(`✅ Metadata saved to server: ${result.path}`)
      } else {
        console.error(`❌ Failed to save metadata: ${response.status}`)
      }
    } catch (error) {
      console.error('❌ Error saving metadata:', error)
    }
  }

  /**
   * ✅ NEW: Load quiz JSON from IndexedDB (not binary database)
   * Dùng để load quiz đã upload dạng JSON, không phải binary SQLite database
   */
  async loadQuizJsonFromIndexedDB(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string,
    loai: 'TN' | 'DS' | 'TL'
  ): Promise<any | null> {
    return new Promise((resolve) => {
      const request = indexedDB.open('TeacherContentDB', 6)

      request.onerror = () => {
        console.error('❌ [LOAD QUIZ JSON] IndexedDB open error:', request.error)
        resolve(null)
      }

      request.onsuccess = () => {
        const db = request.result

        if (!db.objectStoreNames.contains('quizzes')) {
          console.warn('⚠️ [LOAD QUIZ JSON] IndexedDB store "quizzes" not found')
          resolve(null)
          return
        }

        const transaction = db.transaction(['quizzes'], 'readonly')
        const store = transaction.objectStore('quizzes')

        const id = `${teacherId}_L${lop}_C${chuong}_B${bai}_${loai}_json`
        console.log(`🔍 [LOAD QUIZ JSON] Looking for: ${id}`)

        const getRequest = store.get(id)

        getRequest.onsuccess = () => {
          const result = getRequest.result
          if (result && result.data) {
            console.log(`✅ [LOAD QUIZ JSON] Loaded from IndexedDB: ${id}`, {
              questionCount: result.data.questions?.length || 0,
              format: result.format
            })
            resolve(result.data)  // Return JSON object
          } else {
            console.log(`ℹ️ [LOAD QUIZ JSON] No quiz JSON found in IndexedDB: ${id}`)
            resolve(null)
          }
        }

        getRequest.onerror = () => {
          console.error('❌ [LOAD QUIZ JSON] IndexedDB get error:', getRequest.error)
          resolve(null)
        }
      }

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains('quizzes')) {
          db.createObjectStore('quizzes', { keyPath: 'id' })
        }
      }
    })
  }

}

export const teacherContentService = new TeacherContentService()
export default teacherContentService

